import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import { SalonIcon } from "./SalonIcon";
import { SalonsDrawer } from "./SalonsDrawer";
import type {
  SalonOverview,
  SalonSale,
  SalonSaleCollection,
  SalonSaleEligibleAppointment,
  SalonSaleReceipt,
  SalonSalesScreen,
  SalonSchedulingServiceConfig,
} from "./types";

type SalesView = "today" | "pending" | "partial" | "paid" | "all";
type PeriodPreset = "today" | "7d" | "month";
type SalesCommand =
  | { kind: "appointment-sale" }
  | { kind: "manual-sale" }
  | { kind: "collect"; sale: SalonSale }
  | { kind: "detail"; sale: SalonSale }
  | { kind: "cancel"; sale: SalonSale }
  | null;

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudo completar la operación de Ventas.";
}

function dateKey(value: Date | string, timeZone: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return part("year") + "-" + part("month") + "-" + part("day");
}

function addDays(key: string, days: number): string {
  const values = key.split("-").map(Number);
  const date = new Date(
    Date.UTC(values[0]!, values[1]! - 1, values[2]! + days),
  );
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function firstOfMonth(key: string): string {
  return key.slice(0, 7) + "-01";
}

function zoneParts(value: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

function zonedMidnightIso(key: string, timeZone: string): string {
  const values = key.split("-").map(Number);
  const desired = Date.UTC(values[0]!, values[1]! - 1, values[2]!, 0, 0, 0);
  let guess = desired;
  for (let index = 0; index < 3; index += 1) {
    const actual = zoneParts(new Date(guess), timeZone);
    const actualUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    guess += desired - actualUtc;
  }
  return new Date(guess).toISOString();
}

function rangeFor(preset: PeriodPreset, timeZone: string) {
  const today = dateKey(new Date(), timeZone);
  const fromKey =
    preset === "7d"
      ? addDays(today, -6)
      : preset === "month"
        ? firstOfMonth(today)
        : today;
  return {
    from: zonedMidnightIso(fromKey, timeZone),
    to: zonedMidnightIso(addDays(today, 1), timeZone),
    label:
      preset === "7d"
        ? "Últimos 7 días"
        : preset === "month"
          ? "Este mes"
          : "Hoy",
  };
}

function dateTime(
  value: string | null | undefined,
  timeZone: string,
): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function amountMinor(value: string): number | null {
  const parsed = Number(value.trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  const minor = Math.round(parsed * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function paymentStatus(value: SalonSale["paymentStatus"]): string {
  if (value === "paid") return "Cobrado";
  if (value === "partial") return "Parcial";
  if (value === "waived") return "Cortesía";
  return "Pendiente";
}

function paymentMethod(value: SalonSale["paymentMethod"]): string {
  if (value === "cash") return "Efectivo";
  if (value === "transfer") return "Transferencia";
  if (value === "card") return "Tarjeta";
  if (value === "mercadopago") return "Mercado Pago";
  if (value === "mixed") return "Mixto";
  if (value === "other") return "Otro";
  return "Sin definir";
}

function collectionStatus(value: SalonSaleCollection["status"]): string {
  if (value === "approved") return "Aprobado";
  if (value === "pending" || value === "created") return "Pendiente";
  if (value === "partially_refunded") return "Reembolso parcial";
  if (value === "refunded") return "Reembolsado";
  if (value === "rejected") return "Rechazado";
  return "Cancelado";
}

function CommandDrawer(props: {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  // All Sales commands share the same accessible, body-portaled drawer owner
  // used by Services. Never mount an unpositioned form inside the sales table.
  return <SalonsDrawer
    title={props.title}
    eyebrow="Ventas"
    description={props.description}
    onClose={props.onClose}
    width="regular"
  >
    <div className="cd-sales-action-drawer">
      <div className="cd-sales-action-drawer__body">{props.children}</div>
      {props.footer && <footer className="cd-command-drawer__footer cd-sales-action-drawer__footer">
        {props.footer}
      </footer>}
    </div>
  </SalonsDrawer>;
}

function discountInput(
  mode: "none" | "fixed" | "percentage",
  value: string,
  reason: string,
): { type: "fixed" | "percentage"; value: number; reason: string } | null {
  if (mode === "none") return null;
  const normalizedReason = reason.trim();
  if (!normalizedReason) return null;
  if (mode === "fixed") {
    const fixed = amountMinor(value);
    return fixed !== null && fixed > 0
      ? { type: "fixed", value: fixed, reason: normalizedReason }
      : null;
  }
  const percentage = Number(value.trim().replace(",", "."));
  return Number.isSafeInteger(percentage) && percentage >= 1 && percentage <= 100
    ? { type: "percentage", value: percentage, reason: normalizedReason }
    : null;
}

function AppointmentSaleDrawer(props: {
  appointments: readonly SalonSaleEligibleAppointment[];
  timeZone: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (input: {
    appointmentId: string;
    estimatedAmountMinor?: number;
    currency?: string;
    notes: string | null;
    discount?: {
      type: "fixed" | "percentage";
      value: number;
      reason: string;
    } | null;
  }) => Promise<void>;
}) {
  const [appointmentId, setAppointmentId] = useState(
    props.appointments[0]?.id ?? "",
  );
  const [fallbackAmount, setFallbackAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [discountMode, setDiscountMode] =
    useState<"none" | "fixed" | "percentage">("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const selected =
    props.appointments.find((item) => item.id === appointmentId) ?? null;
  const fallbackMinor = amountMinor(fallbackAmount);
  const needsFallbackPrice = selected?.catalogPriceMinor == null;
  const baseMinor = selected?.catalogPriceMinor ?? fallbackMinor;
  const discount = discountInput(
    discountMode,
    discountValue,
    discountReason,
  );
  const discountValid = discountMode === "none" || discount !== null;
  const valid = Boolean(
    selected &&
      baseMinor !== null &&
      baseMinor > 0 &&
      discountValid,
  );

  return <CommandDrawer
    title="Venta desde turno"
    description="El precio del servicio queda registrado al crear la venta. Los descuentos requieren un motivo y se conservan en el historial."
    onClose={props.onClose}
    footer={<>
      <button
        type="button"
        className="cd-button cd-button--secondary"
        onClick={props.onClose}
      >Cancelar</button>
      <button
        type="button"
        className="cd-button cd-button--primary"
        disabled={!valid || props.busy}
        onClick={() => {
          if (!selected) return;
          void props.onCreate({
            appointmentId: selected.id,
            estimatedAmountMinor:
              selected.catalogPriceMinor == null
                ? fallbackMinor ?? undefined
                : undefined,
            currency: selected.currency,
            notes: notes.trim() || null,
            discount,
          });
        }}
      >{props.busy ? "Creando…" : "Crear venta"}</button>
    </>}
  >
    {props.error && <div className="cd-create-sale-command__error" role="alert">
      {props.error}
    </div>}
    <div className="cd-command-form">
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Turno</span>
        <select
          className="cd-input"
          value={appointmentId}
          onChange={(event) => setAppointmentId(event.target.value)}
        >
          {props.appointments.length === 0 &&
            <option value="">No hay turnos elegibles</option>}
          {props.appointments.map((item) =>
            <option key={item.id} value={item.id}>
              {item.customerName + " · " + item.serviceName + " · " +
                dateTime(item.startsAt, props.timeZone)}
            </option>
          )}
        </select>
      </label>
      {selected && <section className="cd-card cd-card--muted">
        <div className="cd-card__body cd-sales-ledger-summary">
          <div><span>Cliente</span><strong>{selected.customerName}</strong></div>
          <div><span>Servicio</span><strong>{selected.serviceName}</strong></div>
          <div><span>Precio catálogo</span><strong>
            {selected.catalogPriceMinor == null
              ? "Sin precio configurado"
              : money(selected.catalogPriceMinor, selected.currency)}
          </strong></div>
        </div>
      </section>}
      {needsFallbackPrice && <label className="cd-command-field cd-field">
        <span className="cd-control-label">Precio de esta venta</span>
        <input
          className="cd-input"
          inputMode="decimal"
          value={fallbackAmount}
          onChange={(event) => setFallbackAmount(event.target.value)}
          placeholder="15000,00"
        />
        <span className="cd-field__hint">
          Este servicio aún no tiene precio canónico. El valor queda como
          snapshot histórico de la línea.
        </span>
      </label>}
      <div className="cd-command-field">
        <span className="cd-control-label">Descuento</span>
        <select
          className="cd-input"
          value={discountMode}
          onChange={(event) =>
            setDiscountMode(event.target.value as typeof discountMode)}
        >
          <option value="none">Sin descuento</option>
          <option value="fixed">Importe fijo</option>
          <option value="percentage">Porcentaje</option>
        </select>
      </div>
      {discountMode !== "none" && <>
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">
            {discountMode === "fixed" ? "Importe del descuento" : "Porcentaje"}
          </span>
          <input
            className="cd-input"
            inputMode="decimal"
            value={discountValue}
            onChange={(event) => setDiscountValue(event.target.value)}
            placeholder={discountMode === "fixed" ? "1000,00" : "10"}
          />
        </label>
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">Motivo obligatorio</span>
          <input
            className="cd-input"
            value={discountReason}
            onChange={(event) => setDiscountReason(event.target.value)}
            placeholder="Promoción, compensación, convenio…"
          />
          <span className="cd-field__hint">
            El motivo y el actor quedan en la evidencia del descuento.
          </span>
        </label>
      </>}
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Nota</span>
        <textarea
          className="cd-input"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </div>
  </CommandDrawer>;
}

type ManualLineDraft = {
  id: string;
  kind: "service" | "custom";
  serviceId: string;
  description: string;
  quantity: string;
  amount: string;
};

function ManualSaleDrawer(props: {
  customers: SalonOverview["customers"];
  services: readonly SalonSchedulingServiceConfig[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (input: {
    customerId?: string | null;
    notes: string | null;
    lines: readonly {
      kind: "service" | "custom";
      serviceId?: string;
      description?: string;
      quantity: number;
      unitPriceMinor?: number;
      currency?: string;
    }[];
    discount?: {
      type: "fixed" | "percentage";
      value: number;
      reason: string;
    } | null;
  }) => Promise<void>;
}) {
  const [customerId, setCustomerId] = useState("");
  const [lines, setLines] = useState<ManualLineDraft[]>([
    {
      id: "line-1",
      kind: "custom",
      serviceId: "",
      description: "",
      quantity: "1",
      amount: "",
    },
  ]);
  const [notes, setNotes] = useState("");
  const [discountMode, setDiscountMode] =
    useState<"none" | "fixed" | "percentage">("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const discount = discountInput(
    discountMode,
    discountValue,
    discountReason,
  );

  const parsedLines = lines.map((line) => {
    const quantity = Number(line.quantity);
    const service = line.kind === "service"
      ? props.services.find((item) => item.id === line.serviceId) ?? null
      : null;
    const fallbackMinor = amountMinor(line.amount);
    const unitPriceMinor = line.kind === "service"
      ? service?.priceMinor ?? fallbackMinor
      : fallbackMinor;
    const currency = line.kind === "service"
      ? service?.currency ?? "ARS"
      : "ARS";
    return {
      line,
      service,
      quantity,
      unitPriceMinor,
      currency,
    };
  });
  const validLines = parsedLines.every(({ line, service, quantity, unitPriceMinor }) =>
    Number.isSafeInteger(quantity) &&
    quantity >= 1 &&
    quantity <= 1000 &&
    unitPriceMinor !== null &&
    unitPriceMinor > 0 &&
    (line.kind === "custom"
      ? line.description.trim().length > 0
      : service !== null && service.active)
  );
  const currencies = new Set(parsedLines.map((line) => line.currency));
  const discountValid = discountMode === "none" || discount !== null;
  const valid =
    lines.length > 0 &&
    validLines &&
    currencies.size <= 1 &&
    discountValid;

  function updateLine(
    id: string,
    patch: Partial<Omit<ManualLineDraft, "id">>,
  ) {
    setLines((current) =>
      current.map((line) => line.id === id ? { ...line, ...patch } : line)
    );
  }

  function addLine(kind: "service" | "custom") {
    setLines((current) => [
      ...current,
      {
        id: `line-${crypto.randomUUID()}`,
        kind,
        serviceId: kind === "service" ? props.services[0]?.id ?? "" : "",
        description: "",
        quantity: "1",
        amount: "",
      },
    ]);
  }

  return <CommandDrawer
    title="Venta manual"
    description="Creá una venta sin turno. No se inventa un Appointment: podés vincular un cliente, usar servicios del catálogo con precio canónico o agregar ítems manuales."
    onClose={props.onClose}
    footer={<>
      <button
        type="button"
        className="cd-button cd-button--secondary"
        onClick={props.onClose}
      >Cancelar</button>
      <button
        type="button"
        className="cd-button cd-button--primary"
        disabled={!valid || props.busy}
        onClick={() => {
          if (!valid) return;
          void props.onCreate({
            customerId: customerId || null,
            notes: notes.trim() || null,
            lines: parsedLines.map(({ line, service, quantity, unitPriceMinor, currency }) =>
              line.kind === "service"
                ? {
                    kind: "service" as const,
                    serviceId: service!.id,
                    quantity,
                    unitPriceMinor:
                      service!.priceMinor == null ? unitPriceMinor! : undefined,
                  }
                : {
                    kind: "custom" as const,
                    description: line.description.trim(),
                    quantity,
                    unitPriceMinor: unitPriceMinor!,
                    currency,
                  }
            ),
            discount,
          });
        }}
      >{props.busy ? "Creando…" : "Crear venta manual"}</button>
    </>}
  >
    {props.error && <div className="cd-create-sale-command__error" role="alert">
      {props.error}
    </div>}
    <div className="cd-command-form">
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Cliente</span>
        <select
          className="cd-input"
          value={customerId}
          onChange={(event) => setCustomerId(event.target.value)}
        >
          <option value="">Venta sin cliente</option>
          {props.customers.map((customer) =>
            <option key={customer.id} value={customer.id}>
              {customer.displayName}
            </option>
          )}
        </select>
        <span className="cd-field__hint">
          El cliente es opcional; si se selecciona, la venta aparecerá en Customer 360.
        </span>
      </label>

      <div className="cd-sales-line-editor">
        <div className="cd-sales-ledger-row">
          <span className="cd-control-label">Líneas de venta</span>
          <div className="cd-sales-lite__actions">
            <button
              type="button"
              className="cd-button cd-button--sm cd-button--ghost"
              disabled={props.services.length === 0}
              onClick={() => addLine("service")}
            >+ Servicio</button>
            <button
              type="button"
              className="cd-button cd-button--sm cd-button--ghost"
              onClick={() => addLine("custom")}
            >+ Ítem manual</button>
          </div>
        </div>

        {lines.map((line, index) => {
          const service = line.kind === "service"
            ? props.services.find((item) => item.id === line.serviceId) ?? null
            : null;
          return <section key={line.id} className="cd-card cd-card--muted">
            <div className="cd-card__body cd-command-form">
              <div className="cd-sales-ledger-row">
                <strong>Línea {index + 1}</strong>
                {lines.length > 1 && <button
                  type="button"
                  className="cd-button cd-button--sm cd-button--ghost"
                  onClick={() =>
                    setLines((current) =>
                      current.filter((item) => item.id !== line.id)
                    )}
                >Quitar</button>}
              </div>

              <label className="cd-command-field cd-field">
                <span className="cd-control-label">Tipo</span>
                <select
                  className="cd-input"
                  value={line.kind}
                  onChange={(event) =>
                    updateLine(line.id, {
                      kind: event.target.value as "service" | "custom",
                      serviceId:
                        event.target.value === "service"
                          ? props.services[0]?.id ?? ""
                          : "",
                      description: "",
                      amount: "",
                    })}
                >
                  <option value="custom">Ítem manual</option>
                  <option value="service" disabled={props.services.length === 0}>
                    Servicio del catálogo
                  </option>
                </select>
              </label>

              {line.kind === "service"
                ? <>
                    <label className="cd-command-field cd-field">
                      <span className="cd-control-label">Servicio</span>
                      <select
                        className="cd-input"
                        value={line.serviceId}
                        onChange={(event) =>
                          updateLine(line.id, { serviceId: event.target.value, amount: "" })}
                      >
                        <option value="">Seleccionar servicio</option>
                        {props.services.filter((item) => item.active).map((item) =>
                          <option key={item.id} value={item.id}>
                            {item.name} · {
                              item.priceMinor == null
                                ? "sin precio"
                                : money(item.priceMinor, item.currency)
                            }
                          </option>
                        )}
                      </select>
                    </label>
                    {service?.priceMinor == null && <label className="cd-command-field cd-field">
                      <span className="cd-control-label">Precio de esta venta</span>
                      <input
                        className="cd-input"
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(event) =>
                          updateLine(line.id, { amount: event.target.value })}
                        placeholder="15000,00"
                      />
                      <span className="cd-field__hint">
                        Este servicio aún no tiene precio canónico; el valor quedará como snapshot histórico.
                      </span>
                    </label>}
                    {service?.priceMinor != null && <p className="cd-field__hint">
                      Precio canónico: {money(service.priceMinor, service.currency)}. El precio queda registrado en la venta aunque luego cambie en el catálogo.
                    </p>}
                  </>
                : <>
                    <label className="cd-command-field cd-field">
                      <span className="cd-control-label">Descripción</span>
                      <input
                        className="cd-input"
                        value={line.description}
                        onChange={(event) =>
                          updateLine(line.id, { description: event.target.value })}
                        placeholder="Producto o ajuste autorizado"
                      />
                    </label>
                    <label className="cd-command-field cd-field">
                      <span className="cd-control-label">Precio unitario</span>
                      <input
                        className="cd-input"
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(event) =>
                          updateLine(line.id, { amount: event.target.value })}
                        placeholder="15000,00"
                      />
                    </label>
                  </>}

              <label className="cd-command-field cd-field">
                <span className="cd-control-label">Cantidad</span>
                <input
                  className="cd-input"
                  inputMode="numeric"
                  value={line.quantity}
                  onChange={(event) =>
                    updateLine(line.id, { quantity: event.target.value })}
                />
              </label>
            </div>
          </section>;
        })}
      </div>

      {currencies.size > 1 && <div className="cd-create-sale-command__error" role="alert">
        Una venta no puede mezclar monedas. Separá los ítems en ventas distintas.
      </div>}

      <section className="cd-card cd-card--muted">
        <div className="cd-card__body cd-sales-ledger-summary">
          <div>
            <span>Subtotal estimado</span>
            <strong>
              {parsedLines.length > 0
                ? money(
                    parsedLines.reduce(
                      (sum, item) =>
                        sum + (item.unitPriceMinor ?? 0) *
                          (Number.isSafeInteger(item.quantity) ? item.quantity : 0),
                      0,
                    ),
                    parsedLines[0]?.currency ?? "ARS",
                  )
                : money(0, "ARS")}
            </strong>
          </div>
        </div>
      </section>

      <div className="cd-command-field">
        <span className="cd-control-label">Descuento global</span>
        <select
          className="cd-input"
          value={discountMode}
          onChange={(event) =>
            setDiscountMode(event.target.value as typeof discountMode)}
        >
          <option value="none">Sin descuento</option>
          <option value="fixed">Importe fijo</option>
          <option value="percentage">Porcentaje</option>
        </select>
      </div>
      {discountMode !== "none" && <>
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">
            {discountMode === "fixed" ? "Importe del descuento" : "Porcentaje"}
          </span>
          <input
            className="cd-input"
            inputMode="decimal"
            value={discountValue}
            onChange={(event) => setDiscountValue(event.target.value)}
            placeholder={discountMode === "fixed" ? "1000,00" : "10"}
          />
        </label>
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">Motivo obligatorio</span>
          <input
            className="cd-input"
            value={discountReason}
            onChange={(event) => setDiscountReason(event.target.value)}
            placeholder="Promoción, compensación, convenio…"
          />
        </label>
      </>}
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Nota</span>
        <textarea
          className="cd-input"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </div>
  </CommandDrawer>;
}

function CollectDrawer(props: {
  sale: SalonSale;
  mercadoPagoConnected: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCollect: (input:
    | {
        kind: "manual";
        method: "cash" | "bank_transfer" | "external_card" | "other";
        amountMinor: number;
        notes: string | null;
        expectedVersion: number;
      }
    | {
        kind: "provider";
        method: "mercadopago";
        amountMinor: number;
        notes: string | null;
        expectedVersion: number;
      }
  ) => Promise<void>;
  onCourtesy: () => Promise<void>;
}) {
  const [channel, setChannel] = useState<"manual" | "mercadopago">("manual");
  const [method, setMethod] = useState<
    "cash" | "bank_transfer" | "external_card" | "other"
  >("cash");
  const [amount, setAmount] = useState(
    (props.sale.pendingAmountMinor / 100).toFixed(2),
  );
  const [notes, setNotes] = useState("");
  const minor = amountMinor(amount);
  const valid = minor !== null &&
    minor > 0 &&
    minor <= props.sale.pendingAmountMinor &&
    (channel !== "mercadopago" || props.mercadoPagoConnected);

  return <CommandDrawer
    title="Cobrar venta"
    description="Registrá cobros por efectivo, transferencia o Mercado Pago. Cada operación conserva su método y fecha."
    onClose={props.onClose}
    footer={<>
      <button
        type="button"
        className="cd-button cd-button--secondary"
        onClick={props.onClose}
      >Cancelar</button>
      <button
        type="button"
        className="cd-button cd-button--ghost"
        disabled={props.busy || props.sale.collectedAmountMinor > 0}
        onClick={() => void props.onCourtesy()}
      >Cortesía</button>
      <button
        type="button"
        className="cd-button cd-button--primary"
        disabled={!valid || props.busy}
        onClick={() => {
          if (minor === null) return;
          if (channel === "mercadopago") {
            void props.onCollect({
              kind: "provider",
              method: "mercadopago",
              amountMinor: minor,
              notes: notes.trim() || null,
              expectedVersion: props.sale.version,
            });
          } else {
            void props.onCollect({
              kind: "manual",
              method,
              amountMinor: minor,
              notes: notes.trim() || null,
              expectedVersion: props.sale.version,
            });
          }
        }}
      >{props.busy
          ? "Procesando…"
          : channel === "mercadopago"
            ? "Crear checkout"
            : "Registrar cobro"}</button>
    </>}
  >
    {props.error && <div className="cd-collect-payment__feedback" role="alert">
      {props.error}
    </div>}
    <section className="cd-card cd-card--muted">
      <div className="cd-card__body cd-sales-ledger-summary">
        <div><span>Total</span><strong>
          {money(props.sale.totalAmountMinor, props.sale.currency)}
        </strong></div>
        <div><span>Cobrado</span><strong>
          {money(props.sale.collectedAmountMinor, props.sale.currency)}
        </strong></div>
        <div><span>Pendiente</span><strong>
          {money(props.sale.pendingAmountMinor, props.sale.currency)}
        </strong></div>
      </div>
    </section>
    <div className="cd-command-form">
      <div className="cd-command-field">
        <span className="cd-control-label">Tipo de cobro</span>
        <div className="cd-control-row">
          <button
            type="button"
            className={"cd-button cd-button--sm " +
              (channel === "manual" ? "cd-button--secondary" : "cd-button--ghost")}
            onClick={() => setChannel("manual")}
          >Manual</button>
          <button
            type="button"
            className={"cd-button cd-button--sm " +
              (channel === "mercadopago" ? "cd-button--secondary" : "cd-button--ghost")}
            disabled={!props.mercadoPagoConnected}
            onClick={() => setChannel("mercadopago")}
          >Mercado Pago</button>
        </div>
        {!props.mercadoPagoConnected &&
          <span className="cd-field__hint">
            Conectá Mercado Pago para habilitar cobros online.
          </span>}
      </div>
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Monto</span>
        <input
          className="cd-input"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>
      {channel === "manual" &&
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">Método manual</span>
          <select
            className="cd-input"
            value={method}
            onChange={(event) => setMethod(event.target.value as typeof method)}
          >
            <option value="cash">Efectivo</option>
            <option value="bank_transfer">Transferencia bancaria</option>
            <option value="external_card">Tarjeta externa/manual</option>
            <option value="other">Otro</option>
          </select>
        </label>}
      {channel === "mercadopago" &&
        <div className="cd-sales-provider-truth">
          El checkout queda <strong>pendiente</strong> hasta que el webhook
          verificado de Mercado Pago confirme el estado. Volver del checkout no
          marca la venta como cobrada.
        </div>}
      <label className="cd-command-field cd-field">
        <span className="cd-control-label">Nota</span>
        <textarea
          className="cd-input"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </div>
  </CommandDrawer>;
}

function ProviderRefundControl(props: {
  collection: SalonSaleCollection;
  busy: boolean;
  onRefund: (collection: SalonSaleCollection, amountMinor?: number) => Promise<void>;
}) {
  const refundableMinor =
    props.collection.amountMinor - props.collection.refundedAmountMinor;
  const [amount, setAmount] = useState("");
  const parsedAmount = amount.trim() ? amountMinor(amount) : null;
  const validPartial =
    parsedAmount !== null &&
    parsedAmount > 0 &&
    parsedAmount <= refundableMinor;

  return <div className="cd-command-form">
    <label className="cd-command-field cd-field">
      <span className="cd-control-label">Reembolso Mercado Pago</span>
      <input
        className="cd-input"
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        placeholder={money(refundableMinor, props.collection.currency)}
      />
      <span className="cd-field__hint">
        Dejalo vacío para reembolsar el saldo completo o ingresá un importe
        menor para un reembolso parcial.
      </span>
    </label>
    <button
      type="button"
      className="cd-button cd-button--sm cd-button--ghost"
      disabled={props.busy || (amount.trim().length > 0 && !validPartial)}
      onClick={() =>
        void props.onRefund(
          props.collection,
          amount.trim().length > 0 ? parsedAmount! : undefined,
        )}
    >{amount.trim().length > 0 ? "Reembolsar importe" : "Reembolsar saldo provider"}</button>
  </div>;
}

function SaleDetailDrawer(props: {
  sale: SalonSale;
  receipt: SalonSaleReceipt | null;
  busy: boolean;
  error: string | null;
  timeZone: string;
  onClose: () => void;
  onReceipt: () => Promise<void>;
  onRefund: (
    collection: SalonSaleCollection,
    amountMinor?: number,
  ) => Promise<void>;
}) {
  return <CommandDrawer
    title="Detalle de venta"
    description="Líneas, descuentos y collections del ledger canónico."
    onClose={props.onClose}
  >
    {props.error && <div className="cd-create-sale-command__error" role="alert">
      {props.error}
    </div>}
    <section className="cd-command-section">
      <h3 className="cd-command-section__title">Líneas</h3>
      <div className="cd-sales-ledger-stack">
        {props.sale.lines.map((line) =>
          <article key={line.id} className="cd-card cd-card--muted">
            <div className="cd-card__body cd-sales-ledger-row">
              <div>
                <strong>{line.description}</strong>
                <span>{line.quantity} × {money(line.unitPriceMinor, props.sale.currency)}</span>
              </div>
              <strong>{money(line.netAmountMinor, props.sale.currency)}</strong>
            </div>
          </article>
        )}
      </div>
    </section>
    {props.sale.discounts.length > 0 && <section className="cd-command-section">
      <h3 className="cd-command-section__title">Descuentos auditados</h3>
      {props.sale.discounts.map((discount) =>
        <div key={discount.id} className="cd-sales-ledger-row">
          <span>{discount.reason}</span>
          <strong>-{money(discount.amountMinor, props.sale.currency)}</strong>
        </div>
      )}
    </section>}
    <section className="cd-command-section">
      <h3 className="cd-command-section__title">Cobros registrados</h3>
      {props.sale.collections.length === 0
        ? <p className="cd-field__hint">Todavía no hay cobros.</p>
        : <div className="cd-sales-ledger-stack">
            {props.sale.collections.map((collection) =>
              <article key={collection.id} className="cd-card cd-card--muted">
                <div className="cd-card__body">
                  <div className="cd-sales-ledger-row">
                    <div>
                      <strong>
                        {collection.kind === "provider"
                          ? "Mercado Pago"
                          : collection.method === "cash"
                            ? "Efectivo"
                            : collection.method === "bank_transfer"
                              ? "Transferencia"
                              : collection.method === "external_card"
                                ? "Tarjeta externa"
                                : "Otro"}
                      </strong>
                      <span>
                        {collectionStatus(collection.status)} · {
                          dateTime(collection.updatedAt, props.timeZone)
                        }
                      </span>
                    </div>
                    <strong>
                      {money(
                        collection.amountMinor - collection.refundedAmountMinor,
                        collection.currency,
                      )}
                    </strong>
                  </div>
                  {collection.kind === "provider" &&
                    collection.paymentId &&
                    ["approved", "partially_refunded"].includes(collection.status) &&
                    collection.refundedAmountMinor < collection.amountMinor &&
                    <ProviderRefundControl
                      collection={collection}
                      busy={props.busy}
                      onRefund={props.onRefund}
                    />}
                </div>
              </article>
            )}
          </div>}
    </section>
    <section className="cd-command-section">
      <div className="cd-sales-ledger-row">
        <span>Subtotal</span>
        <strong>{money(props.sale.subtotalAmountMinor, props.sale.currency)}</strong>
      </div>
      <div className="cd-sales-ledger-row">
        <span>Descuentos</span>
        <strong>-{money(props.sale.discountAmountMinor, props.sale.currency)}</strong>
      </div>
      <div className="cd-sales-ledger-row">
        <span>Total</span>
        <strong>{money(props.sale.totalAmountMinor, props.sale.currency)}</strong>
      </div>
    </section>
    <section className="cd-command-section">
      <button
        type="button"
        className="cd-button cd-button--secondary"
        disabled={props.busy}
        onClick={() => void props.onReceipt()}
      ><SalonIcon name="receipt_long" /> Ver recibo operativo</button>
      {props.receipt && <div className="cd-sales-receipt">
        <div className="cd-sales-ledger-row">
          <span>Recibo</span><strong>{props.receipt.receiptId}</strong>
        </div>
        <div className="cd-sales-ledger-row">
          <span>Total</span>
          <strong>{money(props.receipt.totals.totalAmountMinor, props.receipt.currency)}</strong>
        </div>
        <div className="cd-sales-ledger-row">
          <span>Cobrado</span>
          <strong>{money(props.receipt.totals.collectedAmountMinor, props.receipt.currency)}</strong>
        </div>
        <small>Recibo operativo · no es factura fiscal.</small>
      </div>}
    </section>
  </CommandDrawer>;
}

function CancelSaleModal(props: {
  sale: SalonSale;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCancel: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  return <div className="cd-command-modal is-open">
    <button
      type="button"
      className="cd-command-modal__backdrop"
      aria-label="Cerrar"
      onClick={props.onClose}
    />
    <section
      className="cd-command-modal__panel"
      role="dialog"
      aria-modal="true"
      aria-label="Cancelar venta"
    >
      <header className="cd-command-modal__header">
        <div className="cd-command-modal__header-main">
          <p className="cd-command-modal__eyebrow">Ventas</p>
          <h2 className="cd-command-modal__title">Cancelar venta</h2>
          <p className="cd-command-modal__description">
            Cancelar la venta no devuelve automáticamente pagos realizados por Mercado Pago.
          </p>
        </div>
      </header>
      <div className="cd-command-modal__body cd-cancel-sale-command">
        {props.error && <div className="cd-cancel-sale-command__error" role="alert">
          {props.error}
        </div>}
        <div className="cd-cancel-sale-command__warning">
          La venta y sus cobros permanecen registrados en el historial.
        </div>
        <label className="cd-command-field cd-field">
          <span className="cd-control-label">Motivo</span>
          <textarea
            className="cd-input"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <label className="cd-cancel-sale-command__confirmation">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <span>Entiendo que un refund es una operación separada.</span>
        </label>
      </div>
      <footer className="cd-command-modal__footer">
        <div className="cd-command-modal__footer-actions">
          <button
            type="button"
            className="cd-button cd-button--secondary"
            onClick={props.onClose}
          >Mantener venta</button>
          <button
            type="button"
            className="cd-button cd-button--danger"
            disabled={!confirmed || !reason.trim() || props.busy}
            onClick={() => void props.onCancel(reason.trim())}
          >{props.busy ? "Anulando…" : "Confirmar cancelación"}</button>
        </div>
      </footer>
    </section>
  </div>;
}

export function SalonsSales(props: {
  organizationId: string;
  overview: SalonOverview;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  basePath: string;
}) {
  const [period, setPeriod] = useState<PeriodPreset>("month");
  const [view, setView] = useState<SalesView>("all");
  const [query, setQuery] = useState("");
  const [snapshot, setSnapshot] = useState<SalonSalesScreen | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [command, setCommand] = useState<SalesCommand>(null);
  const [receipt, setReceipt] = useState<SalonSaleReceipt | null>(null);
  const [catalogServices, setCatalogServices] =
    useState<readonly SalonSchedulingServiceConfig[]>([]);
  const range = useMemo(
    () => rangeFor(period, props.overview.timeZone),
    [period, props.overview.timeZone],
  );

  async function reload() {
    setLoading(true);
    try {
      setSnapshot(
        await props.api.salonSales(
          props.organizationId,
          range.from,
          range.to,
          1000,
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    props.api.salonSales(props.organizationId, range.from, range.to, 1000)
      .then((next) => {
        if (current) setSnapshot(next);
      })
      .catch((cause) => {
        if (current) setError(errorText(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [props.api, props.organizationId, range.from, range.to]);

  useEffect(() => {
    let current = true;
    props.api.salonSchedulingSettings(props.organizationId)
      .then((settings) => {
        if (current) {
          setCatalogServices(settings.services.filter((service) => service.active));
        }
      })
      .catch(() => {
        if (current) setCatalogServices([]);
      });
    return () => {
      current = false;
    };
  }, [props.api, props.organizationId]);

  async function mutate(
    operation: () => Promise<unknown>,
    message: string,
    close = true,
  ) {
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      await operation();
      await reload();
      if (close) setCommand(null);
      setFeedback(message);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function connectMercadoPago() {
    setBusy(true);
    setError(null);
    try {
      const result = await props.api.startSalonMercadoPagoOauth(
        props.organizationId,
      );
      window.open(result.authorizationUrl, "_blank", "noopener,noreferrer");
      setFeedback(
        "Autorización abierta en otra pestaña. Al terminar, usá «Actualizar estado».",
      );
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  const today = dateKey(new Date(), props.overview.timeZone);
  const sales = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (snapshot?.sales ?? []).filter((sale) => {
      if (
        view === "today" &&
        dateKey(
          sale.appointmentStartsAt ?? sale.createdAt,
          props.overview.timeZone,
        ) !== today
      ) return false;
      if (
        view === "pending" &&
        !["unpaid", "partial"].includes(sale.paymentStatus)
      ) return false;
      if (view === "partial" && sale.paymentStatus !== "partial") return false;
      if (view === "paid" && sale.paymentStatus !== "paid") return false;
      if (!needle) return true;
      return [
        sale.customerName,
        sale.serviceName,
        sale.notes,
        ...sale.lines.map((line) => line.description),
      ]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLowerCase().includes(needle));
    });
  }, [snapshot, view, query, props.overview.timeZone, today]);

  const primary =
    snapshot?.summary.byCurrency.find((item) => item.currency === "ARS") ??
    snapshot?.summary.byCurrency[0] ??
    null;
  const currency = primary?.currency ?? "ARS";
  const mercadoPago = snapshot?.mercadoPago ?? null;

  return <section className="cd-sales-lite" aria-labelledby="sales-lite-title">
    <div className="cd-page-frame">
      <header className="cd-page-header cd-sales-lite__page-header">
        <div>
          <p className="cd-page-header__eyebrow">Ventas / Cobros</p>
          <h1 id="sales-lite-title" className="cd-page-header__title">Ventas</h1>
          <p className="cd-page-header__description">
            Consultá tus ventas, registrá cobros y seguí los pagos pendientes.
          </p>
          <p className="cd-sales-lite__hero-note">
            <SalonIcon name="verified" />
            <span>Todas las operaciones mantienen su historial.</span>
          </p>
        </div>
        <div className="cd-sales-lite__hero-actions">
          <details className="cd-sales-lite__more">
            <summary>Más acciones</summary>
            <div className="cd-sales-lite__more-menu" role="group" aria-label="Acciones secundarias de ventas">
              <button type="button" onClick={(event) => {
                event.currentTarget.closest("details")?.removeAttribute("open");
                props.navigate(`${props.basePath}/customers?create=1&source=sales`);
              }}><SalonIcon name="person" /> Nuevo cliente</button>
              <button type="button" onClick={(event) => {
                event.currentTarget.closest("details")?.removeAttribute("open");
                setError(null);
                setCommand({ kind: "manual-sale" });
              }}>Venta manual</button>
            </div>
          </details>
          <button
            type="button"
            className="cd-btn-primary cd-page-header-cta"
            onClick={() => {
              setError(null);
              setCommand({ kind: "appointment-sale" });
            }}
          >Nueva venta</button>
        </div>
      </header>

      {loading && <div className="cd-sales-lite__state cd-state-card">
        <p className="cd-sales-lite__state-title">Cargando Ventas…</p>
      </div>}
      {error && !command &&
        <div
          className="cd-sales-lite__state cd-sales-lite__state--error cd-state-card cd-state-card--error"
          role="alert"
        >
          <p className="cd-sales-lite__state-title">No pudimos cargar Ventas.</p>
          <p className="cd-sales-lite__state-text">{error}</p>
        </div>}

      {!loading && snapshot && <div className="cd-sales-lite__content">
        {feedback &&
          <div className="cd-sales-lite__action-status cd-sales-lite__action-status--ok" role="status">
            {feedback}
          </div>}

        <section className="cd-sales-lite__kpis cd-grid" aria-label="Resumen de cobros">
          <article className="cd-sales-lite__kpi">
            <SalonIcon name="bar_chart" className="cd-salons-visual-icon cd-salons-visual-icon--green" />
            <span className="cd-sales-lite__kpi-label">Ventas · {currency}</span>
            <strong>{money(primary?.estimatedAmountMinor ?? 0, currency)}</strong>
          </article>
          <article className="cd-sales-lite__kpi">
            <SalonIcon name="credit_card" className="cd-salons-visual-icon cd-salons-visual-icon--green" />
            <span className="cd-sales-lite__kpi-label">Cobrado</span>
            <strong>{money(primary?.collectedAmountMinor ?? 0, currency)}</strong>
          </article>
          <article className="cd-sales-lite__kpi">
            <SalonIcon name="schedule" className="cd-salons-visual-icon cd-salons-visual-icon--warm" />
            <span className="cd-sales-lite__kpi-label">Pendiente de cobro</span>
            <strong>{money(primary?.pendingAmountMinor ?? 0, currency)}</strong>
          </article>
          <article className="cd-sales-lite__kpi">
            <SalonIcon name="receipt_long" className="cd-salons-visual-icon cd-salons-visual-icon--violet" />
            <span className="cd-sales-lite__kpi-label">Ticket promedio</span>
            <strong>{money(primary?.averageTicketMinor ?? 0, currency)}</strong>
          </article>
        </section>

        <details className="cd-sales-provider-disclosure">
          <summary className="cd-sales-provider-disclosure__summary">
            <span><strong>Mercado Pago</strong><small>{mercadoPago?.connected ? "Conectado" : mercadoPago?.runtimeReady ? "Sin conectar" : "No disponible en esta demo"}</small></span>
            <span className="cd-sales-provider-disclosure__link">Ver conexión</span>
          </summary>
          <div className="cd-sales-provider-disclosure__panel">
          <div className="cd-card__body cd-sales-provider-card__body">
            <div>
              <span className="cd-control-label">Mercado Pago</span>
              <strong>
                {!mercadoPago?.runtimeReady
                  ? "No disponible en esta demo"
                  : mercadoPago.connected
                    ? "Cuenta conectada"
                    : "Sin conectar"}
              </strong>
              <p>
                Los pagos online requieren una conexión activa. ClickDialog no guarda credenciales del proveedor en el navegador.
              </p>
            </div>
            <div className="cd-control-row">
              {mercadoPago?.connected
                ? <button
                    type="button"
                    className="cd-button cd-button--secondary"
                    disabled={busy}
                    onClick={() => void mutate(
                      () => props.api.disconnectSalonMercadoPago(
                        props.organizationId,
                      ),
                      "Mercado Pago desconectado. El historial permanece intacto.",
                    )}
                  >Desconectar</button>
                : <button
                    type="button"
                    className="cd-button cd-button--primary"
                    disabled={busy || !mercadoPago?.runtimeReady}
                    onClick={() => void connectMercadoPago()}
                  >Conectar Mercado Pago</button>}
              <button
                type="button"
                className="cd-button cd-button--ghost"
                disabled={busy}
                onClick={() => void reload()}
              >Actualizar estado</button>
            </div>
          </div>
          </div>
        </details>


        <p className="cd-sales-lite__table-meta">
          Ventas pendientes: {primary?.pendingCount ?? 0} · Ventas cobradas: {
            primary?.paidCount ?? 0
          } · Ingresos estimados según el ledger en {currency}.
        </p>

        <section className="cd-sales-lite__control-surface" aria-label="Controles de ventas">
          <div className="cd-sales-lite__period cd-control-panel">
            <span className="cd-sales-lite__control-label cd-control-label">Período</span>
            <div className="cd-sales-lite__period-chips cd-control-row">
              {([["today", "Hoy"], ["7d", "7 días"], ["month", "Este mes"]] as const)
                .map(([key, label]) =>
                  <button
                    key={key}
                    type="button"
                    className={"cd-sales-lite__chip cd-button cd-button--sm " +
                      (period === key
                        ? "cd-sales-lite__chip--active cd-button--secondary"
                        : "cd-button--ghost")}
                    onClick={() => setPeriod(key)}
                  >{label}</button>
                )}
            </div>
          </div>
          <div className="cd-sales-lite__toolbar">
            <span className="cd-sales-lite__toolbar-label cd-control-label">Vista</span>
            <label className="cd-sales-lite__mobile-view">
              <span>Estado de venta</span>
              <select aria-label="Filtrar ventas por estado" value={view}
                onChange={(event) => setView(event.target.value as SalesView)}>
                <option value="today">Hoy</option>
                <option value="pending">Pendientes</option>
                <option value="partial">Parciales</option>
                <option value="paid">Cobradas</option>
                <option value="all">Todas</option>
              </select>
            </label>
            <div className="cd-sales-lite__tabs cd-tabs">
              {([
                ["today", "Hoy"],
                ["pending", "Pendientes"],
                ["partial", "Parciales"],
                ["paid", "Cobradas"],
                ["all", "Todas"],
              ] as const).map(([key, label]) =>
                <button
                  key={key}
                  type="button"
                  className={"cd-sales-lite__tab cd-tab " +
                    (view === key
                      ? "cd-sales-lite__tab--active cd-tab--active"
                      : "")}
                  aria-pressed={view === key}
                  onClick={() => setView(key)}
                >{label}</button>
              )}
            </div>
          </div>
          <div className="cd-sales-lite__filters cd-control-panel">
            <span className="cd-sales-lite__control-label cd-control-label">Buscar</span>
            <input
              className="cd-input"
              type="search"
              aria-label="Buscar ventas"
              placeholder="Cliente, línea o nota"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </section>

        {sales.length === 0
          ? <div className="cd-sales-lite__empty cd-empty-state">
              <p className="cd-sales-lite__state-title">
                No hay ventas en esta vista.
              </p>
              <p className="cd-sales-lite__state-text">
                Creá una venta desde un turno o una venta manual.
              </p>
            </div>
          : <section className="cd-sales-lite__table-card cd-data-card">
              <div className="cd-sales-lite__table-head cd-data-card__header">
                <div>
                  <h2>Ventas registradas</h2>
                  <p>Importes, cobros realizados y estado de cada venta.</p>
                </div>
                <p className="cd-sales-lite__table-meta">
                  {sales.length} venta{sales.length === 1 ? "" : "s"} · {range.label}
                </p>
              </div>
              <div className="cd-sales-lite__table-scroll">
                <table className="cd-sales-lite__table cd-data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th><th>Cliente</th><th>Concepto</th>
                      <th>Total</th><th>Cobrado</th><th>Pendiente</th>
                      <th>Estado</th><th>Método</th><th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) =>
                      <tr key={sale.id}>
                        <td>{dateTime(
                          sale.appointmentStartsAt ?? sale.createdAt,
                          props.overview.timeZone,
                        )}</td>
                        <td>{sale.customerName ?? "Sin cliente"}</td>
                        <td>{sale.lines[0]?.description ?? sale.serviceName ?? "Venta"}</td>
                        <td>{money(sale.totalAmountMinor, sale.currency)}</td>
                        <td>{money(sale.collectedAmountMinor, sale.currency)}</td>
                        <td>{money(sale.pendingAmountMinor, sale.currency)}</td>
                        <td>
                          <span className={"cd-sales-lite__pill cd-sales-lite__pill--" +
                            (sale.status === "cancelled"
                              ? "cancelled"
                              : sale.paymentStatus)}
                          >
                            {sale.status === "cancelled"
                              ? "Anulada"
                              : paymentStatus(sale.paymentStatus)}
                          </span>
                        </td>
                        <td className="cd-sales-lite__muted">
                          {paymentMethod(sale.paymentMethod)}
                        </td>
                        <td>
                          <div className="cd-sales-lite__actions">
                            <button
                              type="button"
                              className="cd-sales-lite__btn cd-button cd-button--sm"
                              onClick={() => {
                                setReceipt(null);
                                setError(null);
                                setCommand({ kind: "detail", sale });
                              }}
                            >Detalle</button>
                            {sale.status === "active" &&
                              sale.pendingAmountMinor > 0 &&
                              sale.paymentStatus !== "waived" &&
                              <button
                                type="button"
                                className="cd-sales-lite__btn cd-button cd-button--sm"
                                onClick={() => {
                                  setError(null);
                                  setCommand({ kind: "collect", sale });
                                }}
                              >Cobrar</button>}
                            {sale.status === "active" &&
                              <button
                                type="button"
                                className="cd-sales-lite__btn cd-sales-lite__btn--muted cd-button cd-button--sm"
                                onClick={() => {
                                  setError(null);
                                  setCommand({ kind: "cancel", sale });
                                }}
                              >Anular</button>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="cd-sales-lite__mobile-list-wrap">
                <header className="cd-sales-lite__mobile-list-head">
                  <h2 className="cd-sales-lite__mobile-list-title">
                    Cobros operativos
                  </h2>
                  <p className="cd-sales-lite__mobile-list-meta">
                    {sales.length} registros
                  </p>
                </header>
                <div className="cd-sales-lite__mobile-list">
                  {sales.map((sale) =>
                    <article key={sale.id} className="cd-sales-lite__mobile-card">
                      <div className="cd-sales-lite__mobile-card-head">
                        <div>
                          <strong>{sale.customerName ?? "Sin cliente"}</strong>
                          <span>{sale.lines[0]?.description ?? "Venta"}</span>
                        </div>
                        <span className={"cd-sales-lite__pill cd-sales-lite__pill--" +
                          (sale.status === "cancelled"
                            ? "cancelled"
                            : sale.paymentStatus)}
                        >
                          {sale.status === "cancelled"
                            ? "Anulada"
                            : paymentStatus(sale.paymentStatus)}
                        </span>
                      </div>
                      <div className="cd-sales-lite__mobile-card-grid">
                        <div><span>Total</span><strong>
                          {money(sale.totalAmountMinor, sale.currency)}
                        </strong></div>
                        <div><span>Pendiente</span><strong>
                          {money(sale.pendingAmountMinor, sale.currency)}
                        </strong></div>
                      </div>
                      <div className="cd-sales-lite__mobile-actions">
                        <button
                          type="button"
                          className="cd-button cd-button--sm"
                          onClick={() => {
                            setReceipt(null);
                            setCommand({ kind: "detail", sale });
                          }}
                        >Detalle</button>
                        {sale.status === "active" &&
                          sale.pendingAmountMinor > 0 &&
                          sale.paymentStatus !== "waived" &&
                          <button
                            type="button"
                            className="cd-button cd-button--sm"
                            onClick={() => setCommand({ kind: "collect", sale })}
                          >Cobrar</button>}
                      </div>
                    </article>
                  )}
                </div>
              </div>
            </section>}

        <p className="cd-sales-lite__maintenance">
          Los pagos online y su acreditación se consultan desde Mercado Pago. Esta pantalla muestra las ventas y cobros registrados en el negocio.
        </p>
      </div>}
    </div>

    {command?.kind === "appointment-sale" &&
      <AppointmentSaleDrawer
        appointments={snapshot?.eligibleAppointments ?? []}
        timeZone={props.overview.timeZone}
        busy={busy}
        error={error}
        onClose={() => {
          setCommand(null);
          setError(null);
        }}
        onCreate={(input) =>
          mutate(
            () => props.api.createSalonSale(props.organizationId, input),
            "Venta creada con precio snapshot.",
          )}
      />}

    {command?.kind === "manual-sale" &&
      <ManualSaleDrawer
        customers={props.overview.customers}
        services={catalogServices}
        busy={busy}
        error={error}
        onClose={() => {
          setCommand(null);
          setError(null);
        }}
        onCreate={(input) =>
          mutate(
            () => props.api.createManualSalonSale(props.organizationId, input),
            "Venta manual creada sin inventar un turno.",
          )}
      />}

    {command?.kind === "collect" &&
      <CollectDrawer
        sale={command.sale}
        mercadoPagoConnected={mercadoPago?.connected === true}
        busy={busy}
        error={error}
        onClose={() => {
          setCommand(null);
          setError(null);
        }}
        onCollect={(input) =>
          mutate(async () => {
            const result = await props.api.createSalonSaleCollection(
              props.organizationId,
              command.sale.id,
              input,
            );
            if (result.checkout?.checkoutUrl) {
              window.open(
                result.checkout.checkoutUrl,
                "_blank",
                "noopener,noreferrer",
              );
            }
          }, input.kind === "provider"
            ? "Checkout creado. El cobro seguirá pendiente hasta el webhook verificado."
            : "Cobro manual registrado.")
        }
        onCourtesy={() =>
          mutate(
            () => props.api.collectSalonSale(
              props.organizationId,
              command.sale.id,
              {
                mode: "courtesy",
                expectedVersion: command.sale.version,
              },
            ),
            "Venta marcada como cortesía.",
          )}
      />}

    {command?.kind === "detail" &&
      <SaleDetailDrawer
        sale={
          snapshot?.sales.find((sale) => sale.id === command.sale.id) ??
          command.sale
        }
        receipt={receipt}
        busy={busy}
        error={error}
        timeZone={props.overview.timeZone}
        onClose={() => {
          setCommand(null);
          setReceipt(null);
          setError(null);
        }}
        onReceipt={async () => {
          setBusy(true);
          setError(null);
          try {
            setReceipt(
              await props.api.salonSaleReceipt(
                props.organizationId,
                command.sale.id,
              ),
            );
          } catch (cause) {
            setError(errorText(cause));
          } finally {
            setBusy(false);
          }
        }}
        onRefund={(collection, refundAmountMinor) =>
          mutate(
            () => props.api.refundSalonSaleCollection(
              props.organizationId,
              command.sale.id,
              collection.id,
              {
                amountMinor: refundAmountMinor,
                expectedVersion:
                  snapshot?.sales.find((sale) => sale.id === command.sale.id)
                    ?.version ?? command.sale.version,
              },
            ),
            "Se solicitó el reembolso. El cobro original permanece registrado.",
            false,
          )}
      />}

    {command?.kind === "cancel" &&
      <CancelSaleModal
        sale={command.sale}
        busy={busy}
        error={error}
        onClose={() => {
          setCommand(null);
          setError(null);
        }}
        onCancel={(reason) =>
          mutate(
            () => props.api.cancelSalonSale(
              props.organizationId,
              command.sale.id,
              {
                reason,
                expectedVersion: command.sale.version,
              },
            ),
            "Venta anulada. Ningún pago fue reembolsado automáticamente.",
          )}
      />}
  </section>;
}
